import { useResponsiveValue } from '~/hooks/use-responsive-breakpoint';
import type { Rules, Styles } from './getters';

export function useHorizontalListResponsiveValues({
  rules,
  styles,
  defaultThemeV2Config,
}: {
  rules: Rules;
  styles: Styles;
  defaultThemeV2Config: any;
}) {
  const autoscrollSwitch = useResponsiveValue({
    mobile: rules?.autoscroll_mobile_switch,
    tablet: rules?.autoscroll_tablet_switch,
    desktop: rules?.autoscroll_desktop_switch,
    largeDesktop: rules?.autoscroll_large_desktop_switch,
  });

  const autoscrollInterval = useResponsiveValue({
    mobile: rules?.autoscroll_interval_mobile,
    tablet: rules?.autoscroll_interval_tablet,
    desktop: rules?.autoscroll_interval_desktop,
    largeDesktop: rules?.autoscroll_interval_large_desktop,
  });

  const loopSwitch = useResponsiveValue({
    mobile: rules?.loop_mobile_switch,
    tablet: rules?.loop_tablet_switch,
    desktop: rules?.loop_desktop_switch,
    largeDesktop: rules?.loop_large_desktop_switch,
  });

  const arrowsSwitch = useResponsiveValue({
    mobile: styles?.arrows_mobile_switch,
    tablet: styles?.arrows_tablet_switch,
    desktop: styles?.arrows_desktop_switch,
    largeDesktop: styles?.arrows_large_desktop_switch,
  });

  const componentGutter = useResponsiveValue({
    mobile: styles?.component_mobile_gutter_horizontal,
    tablet: styles?.component_tablet_gutter_horizontal,
    desktop: styles?.component_desktop_gutter_horizontal,
    largeDesktop: styles?.component_large_desktop_gutter_horizontal,
  });

  const componentThemeGutter = useResponsiveValue({
    mobile: defaultThemeV2Config.component_mobile_gutter_horizontal,
    tablet: defaultThemeV2Config.component_tablet_gutter_horizontal,
    desktop: defaultThemeV2Config.component_desktop_gutter_horizontal,
    largeDesktop:
      defaultThemeV2Config.component_large_desktop_gutter_horizontal,
  });

  const gutter: number = (componentGutter ?? componentThemeGutter) || 0;

  const componentPaddingLeft = useResponsiveValue({
    mobile: styles?.component_mobile_padding_left,
    tablet: styles?.component_tablet_padding_left,
    desktop: styles?.component_desktop_padding_left,
    largeDesktop: styles?.component_large_desktop_padding_left,
  });

  const componentThemePaddingLeft = useResponsiveValue({
    mobile: defaultThemeV2Config.component_mobile_padding_left,
    tablet: defaultThemeV2Config.component_tablet_padding_left,
    desktop: defaultThemeV2Config.component_desktop_padding_left,
    largeDesktop: defaultThemeV2Config.component_large_desktop_padding_left,
  });

  const paddingLeft = componentPaddingLeft ?? componentThemePaddingLeft;

  const nextCellOffset = useResponsiveValue({
    // @ts-ignore
    mobile: styles?.next_cell_mobile_offset ?? styles?.next_cell_offset_mobile,
    // @ts-ignore
    tablet: styles?.next_cell_tablet_offset ?? styles?.next_cell_offset_tablet,
    desktop:
      // @ts-ignore
      styles?.next_cell_desktop_offset ?? styles?.next_cell_offset_desktop,
    largeDesktop:
      // @ts-ignore
      styles?.next_cell_large_desktop_offset ??
      styles?.next_cell_offset_large_desktop,
  });

  const offScreenOpacitySwitch = useResponsiveValue({
    mobile: styles?.off_screen_opacity_mobile_switch,
    tablet: styles?.off_screen_opacity_tablet_switch,
    desktop: styles?.off_screen_opacity_desktop_switch,
    largeDesktop: styles?.off_screen_opacity_large_desktop_switch,
  });

  const cellScalingSwitch = useResponsiveValue({
    mobile: styles?.mobile_cell_scaling_switch,
    tablet: styles?.tablet_cell_scaling_switch,
    desktop: styles?.desktop_cell_scaling_switch,
    largeDesktop: styles?.large_desktop_cell_scaling_switch,
  });

  const indicatorsAlignment = useResponsiveValue(
    {
      mobile: styles?.mobile_indicators_alignment,
      tablet: styles?.tablet_indicators_alignment,
      desktop: styles?.desktop_indicators_alignment,
      largeDesktop: styles?.large_desktop_indicators_alignment,
    },
    false
  );

  const cellsPerView: number = useResponsiveValue(
    {
      mobile: cellScalingSwitch ? styles?.mobile_cells_per_view : 0,
      tablet: cellScalingSwitch ? styles?.tablet_cells_per_view : 0,
      desktop: cellScalingSwitch ? styles?.desktop_cells_per_view : 0,
      largeDesktop: cellScalingSwitch
        ? styles?.large_desktop_cells_per_view
        : 0,
    },
    false,
    0
  );

  const isOneCellPerView = useResponsiveValue(
    {
      mobile: styles?.mobile_cells_per_view === 1 && cellScalingSwitch,
      tablet: styles?.tablet_cells_per_view === 1 && cellScalingSwitch,
      desktop: styles?.desktop_cells_per_view === 1 && cellScalingSwitch,
      largeDesktop:
        styles?.large_desktop_cells_per_view === 1 && cellScalingSwitch,
    },
    false
  );

  const componentMaxWidth = useResponsiveValue(
    {
      mobile: styles?.component_mobile_maximum_width_switch
        ? styles?.component_mobile_maximum_width ??
          defaultThemeV2Config?.component_mobile_maximum_width
        : 0,
      tablet: styles?.component_tablet_maximum_width_switch
        ? styles?.component_tablet_maximum_width ??
          defaultThemeV2Config?.component_tablet_maximum_width
        : 0,
      desktop: styles?.component_desktop_maximum_width_switch
        ? styles?.component_desktop_maximum_width ??
          defaultThemeV2Config?.component_desktop_maximum_width
        : 0,
      largeDesktop: styles?.component_large_desktop_maximum_width_switch
        ? styles?.component_large_desktop_maximum_width ??
          defaultThemeV2Config?.component_large_desktop_maximum_width
        : 0,
    },
    false,
    0
  );

  const fixedCellWidth = useResponsiveValue(
    {
      mobile: !styles?.mobile_cell_scaling_switch
        ? styles?.cell_mobile_width
        : undefined,
      tablet: !styles?.tablet_cell_scaling_switch
        ? styles?.cell_tablet_width
        : undefined,
      desktop: !styles?.desktop_cell_scaling_switch
        ? styles?.cell_desktop_width
        : undefined,
      largeDesktop: !styles?.large_desktop_cell_scaling_switch
        ? styles?.cell_large_desktop_width
        : undefined,
    },
    false
  );

  return {
    autoscrollSwitch,
    autoscrollInterval,
    loopSwitch,
    arrowsSwitch,
    componentGutter,
    componentPaddingLeft,
    gutter,
    paddingLeft,
    nextCellOffset,
    offScreenOpacitySwitch,
    cellScalingSwitch,
    indicatorsAlignment,
    isOneCellPerView,
    cellsPerView,
    componentMaxWidth,
    fixedCellWidth,
  };
}
